// The link a person pastes to send work to one machine (#6767, epic #6552): a GitHub
// pull-request or issue URL. ONE pure parser, read by the route
// (`apps/api` `POST /v1/runners/:id/work`, and its MCP twin `box send`) and by the Fleet
// card's Send work form (#6770), so the two can never disagree on what a link is.
//
// The URL gives the KIND the role table checks first (`./send-roles.ts`). It is the URL's
// claim, not GitHub's: an `issues/<n>` link whose number is really a pull request is
// refused for an issue role by the route's own read of the issue, after this parse.

import { RepoFullNameSchema } from './task-envelope/envelope';

/** What a parsed link names: `pull/<n>` is `'pr'`, `issues/<n>` is `'issue'`. */
export type WorkLinkKind = 'pr' | 'issue';

/** One parsed link. `repo` is `owner/repo` as written: the route does not fold case. */
export type WorkLink = {
  readonly repo: string;
  readonly number: number;
  readonly kind: WorkLinkKind;
};

/**
 * `[https://][www.]github.com/<owner>/<repo>/(pull|issues)/<n>[/][?…|#…]`, case-insensitive
 * on the fixed parts. The owner/repo halves are re-checked against the envelope's one
 * `RepoFullNameSchema`, so a parsed link always names a repo an envelope can carry.
 */
const WORK_LINK =
  /^(?:https?:\/\/)?(?:www\.)?github\.com\/([^/\s?#]+)\/([^/\s?#]+)\/(pull|issues)\/(\d+)\/?(?:[?#].*)?$/i;

/** The largest issue or pull-request number a send carries: the int column it lands in. */
const MAX_NUMBER = 2_147_483_647;

/** The link's repo, number and kind, or null when it is not a GitHub pull request or issue. */
export function parseWorkLink(link: string): WorkLink | null {
  const m = WORK_LINK.exec(link.trim());
  if (m === null) return null;
  const [, owner, repo, segment, digits] = m;
  const fullName = `${owner}/${repo}`;
  if (!RepoFullNameSchema.safeParse(fullName).success) return null;
  const number = Number(digits);
  if (!Number.isSafeInteger(number) || number < 1 || number > MAX_NUMBER) return null;
  return { repo: fullName, number, kind: segment?.toLowerCase() === 'pull' ? 'pr' : 'issue' };
}
